import React, { useState } from 'react';
import { Share2, Facebook, Linkedin, Mail, Link as LinkIcon, Check } from 'lucide-react';

interface SocialShareProps {
  title: string;
  url: string;
  description?: string;
  className?: string;
}

export default function SocialShare({ title, url, description, className = '' }: SocialShareProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareText = description || title;

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
    setIsOpen(false);
  };

  const handleLinkedInShare = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
    setIsOpen(false);
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(`${shareText}\n\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setIsOpen(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setIsOpen(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setIsOpen(false);
      }, 2000);
    }
  };

  // Use native share API if available (mobile devices)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: shareText,
          url,
        });
        setIsOpen(false);
      } catch (err) {
        // User cancelled or share failed - ignore
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      // Fallback to custom share menu
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={handleNativeShare}
        className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center gap-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Share this fair"
      >
        <Share2 className="h-4 w-4" />
        <span>Share</span>
      </button>

      {/* Dropdown Menu (only shown if native share not available) */}
      {isOpen && !navigator.share && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-20 min-w-[250px]">
            <div className="p-2 space-y-1">
              <button
                onClick={handleFacebookShare}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors flex items-center gap-3"
              >
                <Facebook className="w-5 h-5 text-secondary" />
                <span className="font-medium text-gray-900">Share on Facebook</span>
              </button>

              <button
                onClick={handleLinkedInShare}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors flex items-center gap-3"
              >
                <Linkedin className="w-5 h-5 text-primary-dark" />
                <span className="font-medium text-gray-900">Share on LinkedIn</span>
              </button>

              <button
                onClick={handleEmailShare}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors flex items-center gap-3"
              >
                <Mail className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-900">Share via Email</span>
              </button>

              <div className="border-t border-gray-100 my-1" />

              <button
                onClick={handleCopyLink}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors flex items-center gap-3"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-600">Link Copied!</span>
                  </>
                ) : (
                  <>
                    <LinkIcon className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">Copy Link</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
